import { Settings } from '@streamr/tv-engine/~/settings';
import { Track } from './Track';
export class AppAudioDecoder {
    LOG_PREFIX = '[AppAudioDecoder]';
    audioDecoder;
    frameFifo = [];
    unencodedOutputTrack;
    done = false;
    configured = false;
    decodedFrameCount = 0;
    constructor(trackId) {
        this.unencodedOutputTrack = new Track(trackId, false);
        console.log(`${this.LOG_PREFIX} constructor called`, { trackId });
    }
    async init(errorListener) {
        console.log(`${this.LOG_PREFIX} init called`);
        this.audioDecoder = new AudioDecoder({
            output: (audioData) => {
                const encodedAudioFrame = this.popFrameFromFifo();
                if (!encodedAudioFrame) {
                    console.error(`${this.LOG_PREFIX} No encoded audio frame found in fifo`);
                    audioData.close();
                    return;
                }
                const unencodedAudioFrame = this.createUnencodedAudioFrame(encodedAudioFrame, audioData);
                this.decodedFrameCount++;
                /*
                if (
                    this.decodedFrameCount <= 3 ||
                    this.decodedFrameCount % 100 === 0
                ) {
                    console.log(`${this.LOG_PREFIX} decoded frame`, {
                        frameNumber: this.decodedFrameCount,
                        samples: unencodedAudioFrame.data.length,
                    });
                }*/
                this.unencodedOutputTrack?.emit(unencodedAudioFrame, false, [
                    unencodedAudioFrame.data.buffer,
                ]);
                // Check if we're done and all frames have been processed
                if (this.done && this.frameFifo.length === 0) {
                    this.shutdown().catch(console.error);
                }
            },
            error: (error) => {
                console.error(`${this.LOG_PREFIX} decoder error:`, error);
                errorListener(error);
            },
        });
        // Configure the decoder for Opus
        await this.configureDecoder();
        console.log(`${this.LOG_PREFIX} init completed`);
    }
    async configureDecoder() {
        const config = {
            codec: Settings.audioCodec, // 'opus'
            sampleRate: Settings.audioSampleRate, // 48000
            numberOfChannels: Settings.audioNumberOfChannels, // 1
        };
        // Check if config is supported
        const support = await AudioDecoder.isConfigSupported(config);
        if (!support.supported) {
            throw new Error(`${this.LOG_PREFIX} Audio decoder config not supported: ${JSON.stringify(config)}`);
        }
        this.audioDecoder?.configure(config);
        this.configured = true;
        console.log(`${this.LOG_PREFIX} decoder configured`, { config });
    }
    async destroy() {
        console.log(`${this.LOG_PREFIX} destroy called`);
        if (this.audioDecoder && this.audioDecoder.state !== 'closed') {
            await this.audioDecoder.flush();
            this.audioDecoder.close();
        }
        this.configured = false;
    }
    feedEncodedAudioFrame(frame) {
        if (frame.done) {
            console.log(`${this.LOG_PREFIX} done frame received`);
            if (this.frameFifo.length === 0) {
                this.shutdown().catch(console.error);
            }
            else {
                this.done = true;
            }
            return;
        }
        if (this.done) {
            console.warn(`${this.LOG_PREFIX} is done, cannot feed more frames`);
            return;
        }
        if (!this.configured || !this.audioDecoder) {
            console.warn(`${this.LOG_PREFIX} decoder not configured yet`);
            return;
        }
        try {
            this.pushFrameToFifo(frame);
            const encodedChunk = new EncodedAudioChunk({
                type: 'key', // Opus frames are always key frames
                timestamp: Number(frame.timestamp),
                data: frame.data,
            });
            this.audioDecoder.decode(encodedChunk);
        }
        catch (error) {
            console.error(`${this.LOG_PREFIX} Error decoding frame:`, error, 'frame:', frame.frameNumber);
        }
    }
    async shutdown() {
        console.log(`${this.LOG_PREFIX} shutdown called`, {
            totalDecodedFrames: this.decodedFrameCount,
        });
        await this.destroy();
        this.unencodedOutputTrack?.emit(undefined, true);
        console.log(`${this.LOG_PREFIX} shutdown completed`);
    }
    pushFrameToFifo(frame) {
        this.frameFifo.push(frame);
    }
    popFrameFromFifo() {
        return this.frameFifo.shift();
    }
    createUnencodedAudioFrame(frame, audioData) {
        // Extract audio samples from AudioData
        const samples = new Float32Array(audioData.numberOfFrames);
        audioData.copyTo(samples, { planeIndex: 0 });
        audioData.close();
        return {
            frameNumber: frame.frameNumber,
            timestamp: frame.timestamp,
            duration: frame.duration,
            trackId: frame.trackId,
            data: samples,
            done: frame.done,
            isKeyFrame: frame.isKeyFrame,
        };
    }
    getUnencodedOutputTrack() {
        return this.unencodedOutputTrack;
    }
}
